import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { SHOW_MENU_PRICES } from '../config/ui.ts';

const PaymentModal = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
  
  @media (max-width: 768px) {
    padding: 10px;
    align-items: flex-start;
    padding-top: 20px;
  }
`;

const PaymentContainer = styled.div`
  background: linear-gradient(135deg, var(--white) 0%, rgba(211, 141, 84, 0.10) 100%);
  border-radius: 25px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  position: relative;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 20px;
    max-height: 95vh;
    margin: 10px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: var(--navy-blue);
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    color: var(--brand-gold);
    transform: scale(1.1);
  }
`;

const PaymentHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const YapeLogo = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #722F7F, #9C4A9F);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  font-size: 2rem;
  color: var(--white);
  font-weight: bold;
  box-shadow: 0 10px 30px rgba(114, 47, 127, 0.3);
  
  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
    font-size: 1.5rem;
    border-radius: 15px;
  }
`;

const PaymentTitle = styled.h2`
  color: var(--navy-blue);
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.4rem;
  }
`;

const PaymentSubtitle = styled.p`
  color: #666;
  font-size: 1rem;
`;

const OrderSummary = styled.div`
  background: rgba(227, 227, 221, 0.85);
  border-radius: 15px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  border: 1px solid rgba(139, 69, 19, 0.2);
`;

const OrderTitle = styled.h3`
  color: var(--navy-blue);
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const OrderItem = styled.div`
  display: flex;
  align-items: center;
  padding: 0.8rem 0;
  border-bottom: 1px solid rgba(139, 69, 19, 0.1);
  gap: 12px;
  
  &:last-child {
    border-bottom: none;
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 2px solid rgba(139, 69, 19, 0.2);
    font-weight: 600;
    font-size: 1.1rem;
    justify-content: space-between;
  }
  
  @media (max-width: 768px) {
    padding: 0.6rem 0;
    gap: 8px;
    flex-wrap: wrap;
  }
`;

const ItemName = styled.span`
  color: var(--navy-blue);
  flex: 1;
`;

const ItemQuantity = styled.span`
  color: #666;
  font-size: 0.9rem;
  margin: 0 1rem;
`;

const ItemPrice = styled.span`
  color: var(--brand-gold);
  font-weight: 600;
`;

const ItemControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  
  @media (max-width: 768px) {
    gap: 6px;
  }
`;

const QuantityButton = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px solid #722F7F;
  background: var(--white);
  color: #722F7F;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: #722F7F;
    color: var(--white);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    width: 24px;
    height: 24px;
    font-size: 0.7rem;
  }
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #ff4757;
  cursor: pointer;
  padding: 4px;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.1);
  }
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const YapeSection = styled.div`
  text-align: center;
`;

const YapeInstructions = styled.div`
  background: rgba(114, 47, 127, 0.1);
  border-radius: 15px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid rgba(114, 47, 127, 0.2);
`;

const InstructionTitle = styled.h4`
  color: #722F7F;
  font-weight: 600;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

const InstructionsList = styled.ol`
  text-align: left;
  color: #666;
  line-height: 1.6;
  padding-left: 1rem;
  
  li {
    margin-bottom: 0.5rem;
  }
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding-left: 1.2rem;
  }
`;

const YapeNumber = styled.div`
  background: rgba(227, 227, 221, 0.9);
  border: 2px solid #722F7F;
  border-radius: 15px;
  padding: 1rem;
  margin: 1rem 0;
  text-align: center;
`;

const NumberLabel = styled.div`
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
`;

const PhoneNumber = styled.div`
  font-size: 1.8rem;
  font-weight: 700;
  color: #722F7F;
  letter-spacing: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  
  @media (max-width: 768px) {
    font-size: 1.4rem;
    letter-spacing: 1px;
    flex-direction: column;
    gap: 8px;
  }
`;

const CopyButton = styled.button`
  background: #722F7F;
  color: var(--white);
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 10px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    background: #9C4A9F;
    transform: scale(1.05);
  }
`;


const ConfirmButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #722F7F, #9C4A9F);
  color: var(--white);
  border: none;
  padding: 1rem 2rem;
  border-radius: 25px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  box-shadow: 0 5px 15px rgba(114, 47, 127, 0.3);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(114, 47, 127, 0.4);
  }
  
  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0.8rem 1.5rem;
  }
`;

const StatusMessage = styled.div<{ type: 'success' | 'warning' }>`
  background: ${props => props.type === 'success' ? 
    'linear-gradient(135deg, #4CAF50, #66BB6A)' : 
    'linear-gradient(135deg, #FF9800, #FFB74D)'};
  color: var(--white);
  padding: 1rem;
  border-radius: 15px;
  margin-top: 1rem;
  text-align: center;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 0.8rem;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const ContinueShoppingButton = styled.button`
  flex: 1;
  background: rgba(227, 227, 221, 0.9);
  color: var(--navy-blue);
  border: 2px solid var(--navy-blue);
  padding: 0.8rem 1.5rem;
  border-radius: 20px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  &:hover {
    background: var(--navy-blue);
    color: var(--white);
    transform: translateY(-1px);
  }
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 0.7rem 1.2rem;
  }
`;

const LocationBadge = styled.div`
  background: var(--gradient-gold);
  color: var(--navy-blue);
  padding: 0.5rem 1rem;
  border-radius: 15px;
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 1rem;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    padding: 0.4rem 0.8rem;
  }
`;

interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
}

interface YapePaymentProps {
  isOpen: boolean;
  onClose: () => void;
  cartItems: CartItem[];
  total: number;
  onPaymentComplete: () => void;
  onContinueShopping?: () => void;
  selectedLocation?: string;
  onUpdateQuantity?: (id: string, quantity: number) => void;
  onRemoveItem?: (id: string) => void;
}

const YapePayment: React.FC<YapePaymentProps> = ({
  isOpen,
  onClose,
  cartItems,
  total,
  onPaymentComplete,
  onContinueShopping,
  selectedLocation,
  onUpdateQuantity,
  onRemoveItem
}) => {
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'processing' | 'success'>('pending');
  const [showCopyMessage, setShowCopyMessage] = useState(false);

  const yapeeNumber = "951 234 567"; // Número de ejemplo para YAPE

  useEffect(() => {
    if (isOpen) {
      setPaymentStatus('pending');
    }
  }, [isOpen]);

  const handleCopyNumber = async () => {
    try {
      await navigator.clipboard.writeText(yapeeNumber.replace(/\s/g, ''));
      setShowCopyMessage(true);
      setTimeout(() => setShowCopyMessage(false), 2000);
    } catch (err) {
      console.error('Error al copiar:', err);
    }
  };

  const handleConfirmPayment = () => {
    setPaymentStatus('processing');
    
    // Simular procesamiento del pago
    setTimeout(() => {
      setPaymentStatus('success');
      setTimeout(() => {
        onPaymentComplete();
        onClose();
      }, 3000);
    }, 2000);
  };

  if (!isOpen) return null;

  return (
    <PaymentModal isOpen={isOpen}>
      <PaymentContainer>
        <CloseButton onClick={onClose}>
          <i className="fas fa-times"></i>
        </CloseButton>

        <PaymentHeader>
          <YapeLogo>YAPE</YapeLogo>
          <PaymentTitle>Pagar con YAPE</PaymentTitle>
          <PaymentSubtitle>Método de pago seguro y rápido</PaymentSubtitle>
          {selectedLocation && (
            <LocationBadge>
              <i className="fas fa-map-marker-alt"></i>
              Pepe Tiburón {selectedLocation}
            </LocationBadge>
          )}
        </PaymentHeader>

        {cartItems.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
            <i className="fas fa-shopping-cart" style={{ 
              fontSize: '3rem', 
              color: '#722F7F', 
              marginBottom: '1rem',
              opacity: 0.6 
            }}></i>
            <h3 style={{ 
              color: 'var(--navy-blue)', 
              marginBottom: '0.5rem',
              fontSize: '1.2rem'
            }}>Tu carrito está vacío</h3>
            <p style={{ 
              color: '#666', 
              marginBottom: '1.5rem',
              fontSize: '0.9rem'
            }}>Agrega algunos deliciosos platos marinos</p>
            {onContinueShopping && (
              <ContinueShoppingButton 
                onClick={onContinueShopping}
                style={{ width: '100%', marginBottom: '1rem' }}
              >
                <i className="fas fa-utensils"></i>
                Explorar Menú
              </ContinueShoppingButton>
            )}
          </div>
        ) : (
          <OrderSummary>
            <OrderTitle>
              <i className="fas fa-shopping-cart"></i>
              Resumen del Pedido
            </OrderTitle>
            {cartItems.map((item) => (
              <OrderItem key={item.id}>
                <ItemName>{item.name}</ItemName>
                <ItemQuantity>x{item.quantity}</ItemQuantity>
                {SHOW_MENU_PRICES && (
                  <ItemPrice>S/. {(item.price * item.quantity).toFixed(2)}</ItemPrice>
                )}
                {onUpdateQuantity && onRemoveItem && (
                  <ItemControls>
                    <QuantityButton
                      onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
                      disabled={item.quantity <= 1}
                    >
                      <i className="fas fa-minus"></i>
                    </QuantityButton>
                    <QuantityButton
                      onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                    >
                      <i className="fas fa-plus"></i>
                    </QuantityButton>
                    <RemoveButton onClick={() => onRemoveItem(item.id)}>
                      <i className="fas fa-trash"></i>
                    </RemoveButton>
                  </ItemControls>
                )}
              </OrderItem>
            ))}
            <OrderItem>
              <ItemName>Total a Pagar:</ItemName>
              <ItemQuantity></ItemQuantity>
              {SHOW_MENU_PRICES ? (
                <ItemPrice>S/. {total.toFixed(2)}</ItemPrice>
              ) : (
                <ItemPrice>Consultar</ItemPrice>
              )}
            </OrderItem>
          </OrderSummary>
        )}

        {cartItems.length > 0 && (
          <YapeSection>
            {paymentStatus === 'pending' && (
              <>
                {onContinueShopping && (
                  <ActionButtons>
                    <ContinueShoppingButton onClick={onContinueShopping}>
                      <i className="fas fa-utensils"></i>
                      Seguir Comprando
                    </ContinueShoppingButton>
                  </ActionButtons>
                )}
              
              <YapeInstructions>
                <InstructionTitle>
                  <i className="fas fa-mobile-alt"></i>
                  ¿Cómo pagar con YAPE?
                </InstructionTitle>
                <InstructionsList>
                  <li>Abre tu app de YAPE</li>
                  <li>Selecciona "Yapear"</li>
                  <li>Ingresa el número de teléfono</li>
                  <li>Ingresa el monto exacto</li>
                  <li>Confirma el pago</li>
                  <li>Presiona "Ya Pagué" aquí</li>
                </InstructionsList>
              </YapeInstructions>

              <YapeNumber>
                <NumberLabel>Número de YAPE:</NumberLabel>
                <PhoneNumber>
                  {yapeeNumber}
                  <CopyButton onClick={handleCopyNumber}>
                    <i className="fas fa-copy"></i>
                  </CopyButton>
                </PhoneNumber>
                {showCopyMessage && (
                  <StatusMessage type="success">
                    <i className="fas fa-check"></i>
                    ¡Número copiado!
                  </StatusMessage>
                )}
              </YapeNumber>

              <ConfirmButton onClick={handleConfirmPayment}>
                <i className="fas fa-check-circle"></i>
                Ya Pagué - Confirmar Pedido
              </ConfirmButton>

              <StatusMessage type="warning">
                <i className="fas fa-info-circle"></i>
                Solo presiona "Ya Pagué" después de completar el pago en YAPE
              </StatusMessage>
              </>
            )}

            {paymentStatus === 'processing' && (
              <StatusMessage type="warning">
                <i className="fas fa-spinner fa-spin"></i>
                Procesando tu pedido...
              </StatusMessage>
            )}

            {paymentStatus === 'success' && (
              <StatusMessage type="success">
                <i className="fas fa-check-circle"></i>
                ¡Pedido confirmado! Te contactaremos pronto.
              </StatusMessage>
            )}
          </YapeSection>
        )}
      </PaymentContainer>
    </PaymentModal>
  );
};

export default YapePayment;